import { Controller } from "@hotwired/stimulus"

// Gates the Browser column on the OS permission, and registers a Web Push
// subscription once that permission is granted.
//
// A preference that is enabled while the browser says no is the single most
// confusing state this feature can be in: the checkbox looks on and nothing
// pops up. Enabling therefore asks first, and a denial refuses to save —
// the box goes back off and the browser-level refusal is shown instead.
export default class extends Controller {
  static targets = [ "denied" ]
  static values = { vapidPublicKey: String }

  connect() {
    if (typeof Notification === "undefined") return

    if (Notification.permission === "denied") {
      this.showDenied("Notifications are blocked in this browser. Enable them in your browser settings to use this channel.")
      return
    }

    if (Notification.permission === "granted") this.subscribe()
  }

  async gate(event) {
    const checkbox = event.currentTarget
    if (!checkbox.checked) {
      checkbox.form.requestSubmit()
      return
    }

    if (!("Notification" in window)) {
      this.refuse(checkbox, "This browser does not support notifications.")
      return
    }

    const permission = Notification.permission === "granted"
      ? "granted"
      : await Notification.requestPermission()

    if (permission !== "granted") {
      this.refuse(
        checkbox,
        "Notifications are blocked in this browser. Enable them in your browser settings to use this channel."
      )
      return
    }

    await this.subscribe()
    checkbox.form.requestSubmit()
  }

  refuse(checkbox, message) {
    checkbox.checked = false
    this.showDenied(message)
  }

  showDenied(message) {
    if (!this.hasDeniedTarget) return

    this.deniedTarget.textContent = message
    this.deniedTarget.hidden = false
  }

  async subscribe() {
    if (!this.vapidPublicKeyValue) return
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return

    const registration = await navigator.serviceWorker.register("/service-worker.js")
    await navigator.serviceWorker.ready

    const existing = await registration.pushManager.getSubscription()
    const subscription = existing || await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: this.urlBase64ToUint8Array(this.vapidPublicKeyValue)
    })

    await this.save(subscription)
  }

  async save(subscription) {
    const json = subscription.toJSON()
    const csrf = document.querySelector("meta[name='csrf-token']")?.content
    await fetch("/push_subscriptions", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": csrf
      },
      body: JSON.stringify({
        push_subscription: {
          endpoint: json.endpoint,
          p256dh: json.keys.p256dh,
          auth: json.keys.auth
        }
      })
    })
  }

  urlBase64ToUint8Array(base64) {
    const padding = "=".repeat((4 - (base64.length % 4)) % 4)
    const binary = atob(base64.replace(/-/g, "+").replace(/_/g, "/"))
    const bytes = new Uint8Array(binary.length)
    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index)
    }
    return bytes
  }
}
